import { Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import JobsList from "../../jobs/jobs-list/JobsList";
import { ClientForm } from "../ClientForm";
import { ClientFormCoverages } from "../ClientFormCoverage";
import { ClientInvoicesTab } from "../ClientInvoicesTab";
import { useClients } from "../hooks/useClients";
import { EClientStatus } from "../interfaces/Client.interface";
import { ClientNotes } from "../Clients-notes/ClientNotes";
import { ClientPaymentMethodTab } from "../ClientPaymentMethodTab";

function TabPanel({ value, index, children }) {
  return (
    <div style={{ display: value === index ? "block" : "none" }}>
      <Box sx={{ pt: 2 }}>{children}</Box>
    </div>
  );
}

export function ClientDetailsTabs({
  client = null,
  clientUid = null,
  profileOnly = false,
  onClientUpdated = () => {},
  initialTab = 0,
  defaultTab,
}: {
  client?: any;
  clientUid?: string | null;
  profileOnly?: boolean;
  onClientUpdated?: () => void;
  initialTab?: number | string;
  defaultTab?: number | string;
}) {
  const resolvedInitialTab = defaultTab ?? initialTab;

  const [tab, setTab] = useState(() => {
    if (resolvedInitialTab === "notes" || resolvedInitialTab === 4) return "notes";
    if (resolvedInitialTab === "payment-method") return "payment-method";
    if (resolvedInitialTab === "invoices" || resolvedInitialTab === 3) return "invoices";
    if (resolvedInitialTab === "jobs" || resolvedInitialTab === 2) return "jobs";
    if (resolvedInitialTab === "coverages" || resolvedInitialTab === 1) return "coverages";
    return "info";
  });

  const [clientData, setClientData] = useState(client ?? null);
  const [isLoading, setIsLoading] = useState(!client && !!(clientUid || client?.uid));

  const { getClientByUid } = useClients();
  const resolvedClientUid = clientUid || client?.uid;

  const refreshClient = useCallback(async () => {
    if (!resolvedClientUid) return;
    setIsLoading(true);
    try {
      const response = await getClientByUid(resolvedClientUid);
      if (response?.ok && response?.data) {
        setClientData((prev) => ({
          ...(prev || {}),
          ...response.data,
          status: response.data.status ?? prev?.status ?? client?.status,
        }));
      }
    } finally {
      setIsLoading(false);
    }
  }, [resolvedClientUid, getClientByUid, client?.status]);

  const handleClientUpdate = useCallback(async () => {
    await refreshClient();
    await onClientUpdated?.();
  }, [refreshClient, onClientUpdated]);

  useEffect(() => {
    if (!resolvedClientUid) return;
    refreshClient();
  }, [resolvedClientUid, refreshClient]);

  useEffect(() => {
    if (client) {
      setClientData((prev) => ({ ...(prev || {}), ...client }));
    }
  }, [client]);

  const isProspect = (clientData?.status || client?.status || "").toUpperCase() === EClientStatus.PROSPECT;

  useEffect(() => {
    if (isProspect && (tab === "jobs" || tab === "invoices")) {
      setTab("info");
    }
  }, [isProspect, tab]);

  if (!clientData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        {isLoading ? <CircularProgress size={24} sx={{ color: "#ff8222" }} /> : <Box sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)" }}>Client not found</Box>}
      </Box>
    );
  }
  if (profileOnly) {
    return (
      <Box sx={{ p: 2 }}>
        <ClientForm
          key={clientData.updatedAt ?? clientData.uid}
          client={clientData}
          clientUid={resolvedClientUid}
          mode="update"
          sx={{ p: 0 }}
          onUpdate={handleClientUpdate}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          "& .MuiTab-root": {
            color: "var(--fx-surface-texttertiary, #94A3B8)",
          },
          "& .MuiTab-root.Mui-selected": {
            color: "#ff8222",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#ff8222",
          },
          borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
          width: "fit-content",
        }}
      >
        <Tab label="Client info" value="info" />
        <Tab label="Coverage areas" value="coverages" />
        {!isProspect && <Tab label="Jobs" value="jobs" />}
        {!isProspect && <Tab label="Invoices" value="invoices" />}
        {!isProspect && <Tab label="Payment method" value="payment-method" />}
        <Tab label="Notes" value="notes" />
      </Tabs>

      <TabPanel value={tab} index="info">
        <ClientForm
          key={clientData.updatedAt ?? clientData.uid}
          client={clientData}
          clientUid={resolvedClientUid}
          mode="update"
          sx={{ p: 0 }}
          onUpdate={handleClientUpdate}
        />
      </TabPanel>

      <TabPanel value={tab} index="coverages">
        <ClientFormCoverages
          mode="update"
          clientUid={resolvedClientUid}
          clientAreas={clientData?.coverageAreas || []}
          address={clientData?.address}
          initialSearchAddress={
            clientData?.address
              ? `${clientData.address || ""}${clientData.city ? ", " + clientData.city : ""}${clientData.state ? ", " + clientData.state : ""}${clientData.zipCode ? " " + clientData.zipCode : ""}`.trim()
              : null
          }
          sx={{ p: 0 }}
          onUpdate={refreshClient}
        />
      </TabPanel>

      {!isProspect && (
        <TabPanel value={tab} index="jobs">
          <JobsList clientUid={resolvedClientUid} />
        </TabPanel>
      )}

      {!isProspect && (
        <TabPanel value={tab} index="invoices">
          <ClientInvoicesTab clientUid={resolvedClientUid} />
        </TabPanel>
      )}

      {!isProspect && (
        <TabPanel value={tab} index="payment-method">
          <ClientPaymentMethodTab clientUid={resolvedClientUid} />
        </TabPanel>
      )}

      <TabPanel value={tab} index="notes">
        <ClientNotes clientUid={resolvedClientUid} />
      </TabPanel>
    </Box>
  );
}
